import React, {
  useEffect, useState, useRef, useMemo, useCallback,
} from 'react';
import { Icon } from 'anf-core-react';
import { createRoot } from 'react-dom/client';
import useScript from '../../../server/html/ScriptHook';
import { styliticsBrandToThemeBrand, getStyliticsBrand } from '../../helpers/styliticsUtils';

function StyliticsScript({
  brand,
  kicId,
  tmntData,
  isMobile,
  siteRegion,
  locale,
  quickViewMfeFlag,
}) {
  const styliticsContainerRef = useRef(null);
  const widgetRef = useRef(null);
  const [hasStylitics, setHasStylitics] = useState(false);

  const scriptURL = 'https://web-assets.stylitics.com/v3-hotspots/latest/hotspots.release.js';
  const [scriptLoaded, scriptError] = useScript(scriptURL);

  const styliticsBrand = useMemo(
    () => getStyliticsBrand(brand, siteRegion),
    [brand, siteRegion],
  );

  const cleanupWidget = useCallback(() => {
    if (widgetRef.current?.destroy) {
      widgetRef.current.destroy();
      widgetRef.current = null;
    }
  }, []);

  useEffect(() => {
    cleanupWidget();

    if (scriptLoaded && !scriptError) {
      widgetRef.current = new window.StyliticsHotspotsWidget(
        styliticsBrand,
        'mfe-stylitics-container',
        {
          api: {
            item_number: kicId,
            min: 3,
            max: 3,
            ...(brand === 'gh' && { tags: 'gillyhicks_outfit' }),
          },
          display: {
            bundleBackgroundColor: 'transparent',
            clickableCarouselDots: true,
            disableMnM: true,
            swipeableCarouselDots: true,
          },
          price: { hideDoubleZeroCents: true },
          text: { viewDetailsCTA: tmntData.styliticsViewDetails.value },
          customer: { locale },
        },
      );

      setupWidgetListeners(widgetRef.current);
      if (kicId) widgetRef.current.start();
    }

    return cleanupWidget;
  }, [
    scriptLoaded,
    scriptError,
    styliticsBrand,
    kicId,
    brand,
    tmntData.styliticsViewDetails.value,
    locale,
    cleanupWidget,
    setupWidgetListeners,
  ]);

  useEffect(() => {
    if (widgetRef.current) {
      widgetRef.current.refresh({ api: { item_number: kicId } });
    }
  }, [kicId]);

  useEffect(() => {
    const handleQuickviewAddToBag = (e) => {
      const eventKicId = e.detail?.kicId;
      if (widgetRef.current && typeof widgetRef.current.trackAddItemToCart === 'function') {
        widgetRef.current.trackAddItemToCart(eventKicId);
      }
    };
    window.addEventListener('styliticsV3Item:addToBagForMFE', handleQuickviewAddToBag);
    return () => {
      window.removeEventListener('styliticsV3Item:addToBagForMFE', handleQuickviewAddToBag);
    };
  }, []);

  const setupWidgetListeners = useCallback(
    (widgetInstance) => {
      widgetInstance.on('mount', 'bundle', onLoadSingleBundle);
      widgetInstance.on('mount', 'bundles', onLoadBundlesMfe);
      widgetInstance.on('mount', 'item', onLoadItem);
      widgetInstance.override('click', 'item', handleStyliticsItemClick);
    },
    [onLoadSingleBundle, onLoadBundlesMfe, onLoadItem, handleStyliticsItemClick],
  );

  const onLoadSingleBundle = useCallback(
    (outfit) => {
      initializeCtaIconAndClickListener(outfit.element, styliticsBrand);
      if (outfit.bundle && outfit.bundle['on-model-image']) {
        setupBadge('.stylitics-bundle-badge', tmntData.styliticsModelIsWearing.value);
        setupBadge('.stylitics-bundle-badge-modal', tmntData.styliticsModelIsWearing.value);
      }
    },
    [styliticsBrand, tmntData.styliticsModelIsWearing.value],
  );

  const onLoadBundlesMfe = useCallback((data) => {
    if (Array.isArray(data.bundles) && data.bundles.length > 2) {
      setHasStylitics(true);
      window.digitalData.set('bundles', data.bundles);
    } else {
      setHasStylitics(false);
      window.digitalData.set('bundles', false);
    }
  }, []);

  const onLoadItem = useCallback((data) => {
    if (data?.item) {
      replaceImages(data);
    }
  }, []);

  const handleStyliticsItemClick = useCallback((data) => {
    const itemClicked = data.item.other_client_item_ids;
    let quickviewData = {
      collectionId: itemClicked.collection_ids[0],
      productId: itemClicked.product_ids[0],
      returnFocus: getReturnFocus(data.element),
      seq: itemClicked.csequence_ids[0],
      faceout: 'prod',
    };

    if (quickViewMfeFlag) {
      const handleOnModalPrimaryActionSuccess = () => {
        document.dispatchEvent(new CustomEvent('styliticsV3Item:addToBagForMFE', {
          detail: {
            kicId,
          },
        }));
      };
      quickviewData = {
        ...quickviewData,
        productViewMethod: 'recommendations-stylitics',
        pageViewMethod: 'recommendations',
        onModalPrimaryActionSuccess: handleOnModalPrimaryActionSuccess,
      };
      document.dispatchEvent(new CustomEvent('quickview:mfe:openModal', { detail: quickviewData }));
    } else {
      quickviewData = {
        ...quickviewData,
        isStylitics: true,
        imageId: `${data.item.remote_id}_prod1`,
        productName: data.item.name,
        productBrand: styliticsBrandToThemeBrand[data.item.brand],
      };
      window.dispatchEvent(new CustomEvent('quickview:openModal', { detail: quickviewData }));
    }
  }, [getReturnFocus, quickViewMfeFlag, kicId]);

  const initializeCtaIconAndClickListener = (widgetElement, brandName) => {
    const ctaEyeIconContainer = widgetElement.querySelector('.stylitics-view-items-cta-icon');
    const ctaEyeIconWrapper = widgetElement.querySelector('.stylitics-cta');
    if (ctaEyeIconContainer && ctaEyeIconWrapper) {
      ctaEyeIconWrapper.addEventListener('click', () => {
        window.digitalData.trigger('universal_click', {
          tealium_event: 'click',
          data_text: 'stylitics-view-this-look',
          data_action: 'open',
        });
      });
      const existingIcon = ctaEyeIconContainer.querySelector('svg');
      if (existingIcon) existingIcon.remove();
      const iconKey = brandName.includes('abercrombie') ? 'eye-filled-anf' : 'eye-hco';
      createRoot(ctaEyeIconContainer).render(<Icon icon={iconKey} />);
    }
  };

  const replaceImages = (data) => {
    const originalUrl = data.item.client_original_image_url;
    const img = data.element.querySelector('.stylitics-item-image');
    if (img && originalUrl) {
      const imageURL = `${originalUrl}?policy=product-small`;
      img.setAttribute('data-src', imageURL);
      img.setAttribute('data-testid', 'stylitics-item-image');
    }
  };

  const setupBadge = (selector, text) => {
    const badgeContainerElements = document.querySelectorAll(selector);
    badgeContainerElements.forEach((element) => {
      const clonedElement = element.cloneNode(true);
      clonedElement.innerHTML = '';
      const badgeContainer = document.createElement('div');
      createRoot(badgeContainer).render(
        <span className="stylitics-bundle-badge-js">{text}</span>,
      );
      clonedElement.appendChild(badgeContainer);
      element.parentNode.replaceChild(clonedElement, element);
      clonedElement.setAttribute('data-testid', 'stylitics-bundle-badge');
    });
  };

  const getReturnFocus = useCallback((item) => {
    let returnFocus = item;
    if (returnFocus && returnFocus.classList.contains('stylitics-overlay-container')) {
      returnFocus = returnFocus
        .closest('.stylitics-bundle-container')
        ?.querySelector('.stylitics-cta');
    } else if (returnFocus && returnFocus.classList.contains('stylitics-item-container')) {
      returnFocus = returnFocus.querySelector('.stylitics-cta-link');
    }
    return returnFocus;
  }, []);

  return (
    <div
      ref={styliticsContainerRef}
      className={`stylitics-main-container ${hasStylitics ? '' : 'hide'}`}
    >
      <section
        className={`stylitics-widget-wrapper ${
          siteRegion === 'EU' ? 'hide-price' : ''
        }`}
        id="mfe-stylitics-container"
        data-testid="mfe-stylitics"
      >
        <div className="stylitics-titles-wrapper">
          <h2 className="stylitics-title h1">
            {tmntData.styliticsHowToStyle.value}
          </h2>
          <span className="stylitics-subtitle">
            {tmntData.styliticsFavoriteWaysToWear.value}
          </span>
          {isMobile && (
            <span className="stylitics-mobile-tip">
              {tmntData.styliticsTapToReveal.value}
            </span>
          )}
        </div>
      </section>
    </div>
  );
}

export default StyliticsScript;
